import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type { RecipientGroupSummary, UserSummary } from '@todo/shared';
import {
  RECIPIENT_GROUP_DESCRIPTION_MAX,
  RECIPIENT_GROUP_NAME_MAX,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { recipientGroupApi } from '../api/recipient-group.api';
import { userApi } from '../api/user.api';

/**
 * Recipient Groups (Manage) - named lists of people to invite.
 *
 * THIS PAGE IS THE ONLY PLACE A GROUP CHANGES. The meeting form reads
 * groups and copies their members into its attendee list, but never writes
 * back: adding somebody to one meeting does not add them to the group.
 * That one-way rule is the whole reason a group stays trustworthy, so the
 * page says it out loud rather than leaving people to discover it.
 *
 * Deleting is a SOFT delete on the server, and meetings already created
 * are untouched - their attendees were copied, never referenced. The name
 * becomes free for reuse immediately.
 */
export default function RecipientGroupsPage() {
  const [groups, setGroups] = useState<RecipientGroupSummary[]>([]);
  const [people, setPeople] = useState<UserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editMembers, setEditMembers] = useState<string[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  /* The dropdown opens on FOCUS, not on the first keystroke - same as the
     meeting form's AttendeePicker. Someone adding people to a group mostly
     wants to browse the org, not guess at names they already half-know. */
  const [memberOpen, setMemberOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupList, peopleList] = await Promise.all([
        recipientGroupApi.list(),
        userApi.list({ status: 'ACTIVE', pageSize: 100 }),
      ]);
      setGroups(groupList);
      setPeople(peopleList.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load recipient groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    try {
      const group = await recipientGroupApi.create({ name });
      setNewName('');
      setFlash(`${name} created. Add people to it below.`);
      await load();
      // Straight into editing: a group with no members is not useful yet.
      startEdit(group);
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not create the group');
    } finally {
      setCreateBusy(false);
    }
  }

  function startEdit(group: RecipientGroupSummary) {
    setEditingId(group.id);
    setEditName(group.name);
    setEditDescription(group.description ?? '');
    setEditMembers(group.members.map((m) => m.userId));
    setMemberSearch('');
    setMemberOpen(false);
  }

  async function save(group: RecipientGroupSummary) {
    const name = editName.trim();
    if (!name) return;
    setBusyId(group.id);
    try {
      /* userIds always goes, because this form owns the whole membership -
         the server treats its presence as "replace the lot". */
      await recipientGroupApi.update(group.id, {
        name,
        description: editDescription.trim() || null,
        userIds: editMembers,
      });
      setEditingId(null);
      setFlash(`${name} saved.`);
      void load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not save the group');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(group: RecipientGroupSummary) {
    setBusyId(group.id);
    try {
      await recipientGroupApi.remove(group.id);
      setConfirmDeleteId(null);
      setFlash(`${group.name} deleted. Meetings already created keep their attendees.`);
      void load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not delete the group');
    } finally {
      setBusyId(null);
    }
  }

  const memberMatches = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    return people
      .filter((p) => !editMembers.includes(p.id))
      .filter((p) => !term || p.displayName.toLowerCase().includes(term));
  }, [people, editMembers, memberSearch]);

  const byId = useMemo(
    () => new Map(people.map((p) => [p.id, p])),
    [people],
  );

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <h1 className="text-lg font-semibold text-slate-900">Recipient groups</h1>
      <p className="mt-1 text-sm text-slate-500">
        A named list of people you invite together. Choosing a group on a
        meeting or engagement <strong>adds</strong> its members to the
        attendees - you can still add or remove individuals there, and doing
        so never changes the group. Groups are edited here only.
      </p>

      {flash && (
        <p className="mt-3 rounded-lg border border-brand-200 bg-brand-50 px-3 py-2 text-sm text-brand-800">
          {flash}
        </p>
      )}
      {error && (
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      <form onSubmit={create} className="mt-5 flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={RECIPIENT_GROUP_NAME_MAX}
          placeholder="New group name, e.g. Monday review"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        <button
          type="submit"
          disabled={createBusy || !newName.trim()}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          Create
        </button>
      </form>

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Loading...</p>
      ) : groups.length === 0 ? (
        <p className="mt-6 rounded-lg border border-dashed border-slate-300 px-3 py-6 text-center text-sm text-slate-500">
          No groups yet. Create one above and add the people who usually
          attend together.
        </p>
      ) : (
        <ul className="mt-5 space-y-3">
          {groups.map((group) => (
            <li key={group.id} className="rounded-xl border border-slate-200 bg-white p-4">
              {editingId === group.id ? (
                <div className="space-y-3">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={RECIPIENT_GROUP_NAME_MAX}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium outline-none focus:border-brand-500"
                  />
                  <input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    maxLength={RECIPIENT_GROUP_DESCRIPTION_MAX}
                    placeholder="What this group is for (optional)"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />

                  {editMembers.length > 0 && (
                    <ul className="flex flex-wrap gap-1.5">
                      {editMembers.map((id) => (
                        <li key={id}>
                          <button
                            type="button"
                            onClick={() => setEditMembers(editMembers.filter((m) => m !== id))}
                            className="rounded-full border border-slate-300 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                          >
                            {byId.get(id)?.displayName ?? 'Unknown person'} ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="relative">
                    <input
                      value={memberSearch}
                      onChange={(e) => { setMemberSearch(e.target.value); setMemberOpen(true); }}
                      onFocus={() => setMemberOpen(true)}
                      /* The delay lets the option's own click land first -
                         blur fires before click, so closing immediately
                         would swallow every selection. */
                      onBlur={() => setTimeout(() => setMemberOpen(false), 120)}
                      placeholder="Search people, or click to see everyone"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                    />

                    {memberOpen && (
                      <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                        {memberMatches.length === 0 ? (
                          <li className="px-3 py-2 text-sm text-slate-500">
                            {people.length === 0
                              ? 'No people loaded.'
                              : memberSearch.trim()
                                ? 'Nobody else matches.'
                                : 'Everyone is already in this group.'}
                          </li>
                        ) : (
                          memberMatches.slice(0, 50).map((person) => (
                            <li key={person.id}>
                              <button
                                type="button"
                                /* preventDefault keeps focus on the input,
                                   so you can add several in a row without
                                   clicking back into the field each time. */
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  setEditMembers([...editMembers, person.id]);
                                  setMemberSearch('');
                                }}
                                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition hover:bg-slate-50"
                              >
                                <span className="truncate text-slate-800">{person.displayName}</span>
                                <span className="shrink-0 truncate text-xs text-slate-500">
                                  {person.jobTitle ?? person.roleName}
                                </span>
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busyId === group.id}
                      onClick={() => void save(group)}
                      className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingId(null)}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{group.name}</p>
                    {group.description && (
                      <p className="mt-0.5 text-xs text-slate-500">{group.description}</p>
                    )}
                    <p className="mt-1.5 text-sm text-slate-600">
                      {group.memberCount === 0
                        ? 'No members yet'
                        : group.members.map((m) => m.displayName).join(', ')}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(group)}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                    {confirmDeleteId === group.id ? (
                      <button
                        type="button"
                        disabled={busyId === group.id}
                        onClick={() => void remove(group)}
                        className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        Really delete
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(group.id)}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:border-rose-300 hover:text-rose-700"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
